import { createRoute, lazyRouteComponent, notFound } from '@tanstack/react-router';
import { z } from 'zod';
import { labelFrom } from '@/app/breadcrumbs';
import { appRoute, type RouterContext } from '@/app/layout-routes';
import { requirePermissions } from '@/app/route-guards';
import { filterParam } from '@/lib/url-state';
import { brandQuery, brandsListQuery, brandsSearchSchema, isBrandNotFound } from './api';
import { BrandNotFound } from './pages/brand-not-found';

/** Brand ids are positive integers; anything else is a missing page, not a request. */
const parseId = (raw: string) => {
  const id = Number(raw);
  if (!Number.isInteger(id) || id <= 0) throw notFound();
  return id;
};

async function loadBrand(context: RouterContext, rawId: string) {
  try {
    return await context.queryClient.ensureQueryData(brandQuery(parseId(rawId)));
  } catch (error) {
    if (isBrandNotFound(error)) throw notFound();
    throw error;
  }
}

const crumb = (params: Record<string, string>, brand: unknown) => ({
  label: labelFrom(brand, 'Name') ?? `Brand #${params.brandId ?? ''}`,
});

export const brandsListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'brands',
  validateSearch: brandsSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_BRANDS'),
  loaderDeps: ({ search }) => search,
  // Starts the fetch without blocking: the page shows the previous rows, dimmed, until it lands.
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(brandsListQuery(deps));
  },
  component: lazyRouteComponent(() => import('./pages/brands-list-page'), 'BrandsListPage'),
});

export const brandCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'brands/new',
  /** `?operatorId=13` preselects the operator (the operator page's "Add brand"). */
  validateSearch: z.object({ operatorId: filterParam.number() }),
  beforeLoad: ({ context }) => requirePermissions(context, 'UPDATE_BRANDS'),
  staticData: { crumbs: () => [{ label: 'New brand' }] },
  component: lazyRouteComponent(() => import('./pages/brand-create-page'), 'BrandCreatePage'),
});

export const brandDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'brands/$brandId',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_BRANDS'),
  loader: ({ context, params }) => loadBrand(context, params.brandId),
  staticData: { crumbs: (params, brand) => [crumb(params, brand)] },
  notFoundComponent: BrandNotFound,
  component: lazyRouteComponent(() => import('./pages/brand-detail-page'), 'BrandDetailPage'),
});

export const brandEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'brands/$brandId/edit',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_BRANDS', 'UPDATE_BRANDS'),
  loader: ({ context, params }) => loadBrand(context, params.brandId),
  staticData: {
    crumbs: (params, brand) => [
      { ...crumb(params, brand), href: `/brands/${encodeURIComponent(params.brandId ?? '')}` },
      { label: 'Edit' },
    ],
  },
  notFoundComponent: BrandNotFound,
  component: lazyRouteComponent(() => import('./pages/brand-edit-page'), 'BrandEditPage'),
});

/** Routes for this feature, attached to `appRoute`; `app/routeTree.ts` spreads them in. */
export const brandsRoutes = [brandsListRoute, brandCreateRoute, brandDetailRoute, brandEditRoute] as const;
